import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Car, User, Briefcase, Play, Filter } from 'lucide-react';
import { apiFetch } from '../lib/api';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

const stateNames = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia'
};

const TeamSection = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedState, setSelectedState] = useState('ALL');

  useBodyScrollLock(Boolean(selectedMember));

  useEffect(() => {
    let isMounted = true;

    const loadTeamMembers = async () => {
      try {
        const response = await apiFetch('/api/team/members?limit=100&auto_import=true');
        if (!response.ok) {
          throw new Error('team members request failed');
        }

        const members = await response.json();
        if (!Array.isArray(members)) {
          throw new Error('invalid team members payload');
        }

        if (isMounted) {
          setTeamMembers(members);
          setLoadError(false);
        }
      } catch {
        if (isMounted) {
          setTeamMembers([]);
          setLoadError(true);
          setSelectedMember(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingMembers(false);
        }
      }
    };

    loadTeamMembers();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedMember) {
      return;
    }

    const memberStillExists = teamMembers.some(
      (member) => String(member.id) === String(selectedMember.id)
    );
    if (!memberStillExists) {
      setSelectedMember(null);
    }
  }, [teamMembers, selectedMember]);
  
  const uniqueStates = useMemo(() => {
    const states = [...new Set(teamMembers.map(member => member.state))];
    return states.sort();
  }, [teamMembers]);
  
  const filteredMembers = useMemo(() => {
    if (selectedState === 'ALL') return teamMembers;
    return teamMembers.filter(member => member.state === selectedState);
  }, [selectedState, teamMembers]);

  const isEmbeddableVideo = (url) => {
    if (!url || typeof url !== 'string') return false;
    return (
      url.includes('youtube.com/embed/') ||
      url.includes('player.vimeo.com/video/')
    );
  };

  const hasVideoUrl = (url) => {
    return typeof url === 'string' && url.trim().length > 0;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <section id="team" className="py-24 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.2 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-red-500/20 text-red-400 rounded-full text-sm font-medium mb-4">
            The Chasers
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Meet Our Team
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {teamMembers.length} dedicated storm chasers bringing you the most incredible weather documentation. Click on any member to learn more.
          </p>
        </motion.div>

        {loadError ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 rounded-2xl bg-gray-900/50 border border-gray-800"
          >
            <p className="text-gray-300 text-lg">
              Unable to retrieve team members, please try again later.
            </p>
          </motion.div>
        ) : (
          <>
            {/* State Filter */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2 }}
              className="flex flex-wrap items-center justify-center gap-3 mb-12"
            >
              <div className="flex items-center gap-2 text-gray-400 mr-2">
                <Filter size={18} />
                <span className="text-sm font-medium">Filter by State:</span>
              </div>
              
              <button
                onClick={() => setSelectedState('ALL')}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedState === 'ALL'
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/25'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                All States
                <span className="ml-2 px-2 py-0.5 rounded-full bg-black/20 text-xs">
                  {teamMembers.length}
                </span>
              </button>
              
              {uniqueStates.map((state) => {
                const count = teamMembers.filter(m => m.state === state).length;
                return (
                  <button
                    key={state}
                    onClick={() => setSelectedState(state)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      selectedState === state
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/25'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    {stateNames[state] || state}
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-black/20 text-xs">
                      {count}
                    </span>
                  </button>
                );
              })}
            </motion.div>

            {isLoadingMembers ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <p className="text-gray-400 text-lg">Loading team members...</p>
              </motion.div>
            ) : (
              <>
                {/* Team Grid */}
                <motion.div
                  layout
                  className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6"
                >
                  <AnimatePresence mode="popLayout">
                    {filteredMembers.map((member) => (
                      <motion.div
                        key={member.id}
                        layout
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3 }}
                        className="group relative cursor-pointer"
                        onClick={() => setSelectedMember(member)}
                      >
                        <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-800">
                          <img
                            src={member.image}
                            alt={member.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300" />
                          
                          {/* State Badge */}
                          <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-full">
                            <span className="text-white text-xs font-medium">{member.state}</span>
                          </div>
                          
                          {/* Member Info */}
                          <div className="absolute bottom-0 left-0 right-0 p-4">
                            <h3 className="text-white font-bold text-sm sm:text-base truncate">
                              {member.name}
                            </h3>
                            <p className="text-red-400 text-xs sm:text-sm font-medium truncate">
                              {member.role}
                            </p>
                          </div>

                          {/* Hover Overlay with Plus Icon */}
                          <div className="absolute inset-0 flex items-center justify-center bg-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="w-12 h-12 rounded-full bg-red-500/80 flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
                              <span className="text-white text-xl font-bold">+</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>

                {/* No Results Message */}
                {filteredMembers.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-12"
                  >
                    <p className="text-gray-400 text-lg">No team members found in this state.</p>
                  </motion.div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Team Member Detail Modal */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
            onClick={() => setSelectedMember(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl max-h-[90vh] bg-gray-900 rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedMember(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-red-500 rounded-full flex items-center justify-center text-white transition-colors duration-200"
              >
                <X size={20} />
              </button>

              <div className="flex flex-col lg:flex-row max-h-[90vh] overflow-y-auto">
                {/* Left Side - Photo & Video */}
                <div className="lg:w-2/5 flex-shrink-0">
                  {/* Member Photo */}
                  <div className="relative aspect-square lg:aspect-auto lg:h-72">
                    <img
                      src={selectedMember.image}
                      alt={selectedMember.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent lg:bg-gradient-to-r" />
                  </div>
                  
                  {/* Vehicle Video */}
                  <div className="p-4 lg:p-6">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Play size={18} className="text-red-400" />
                      Vehicle Showcase
                    </h4>
                    <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-800">
                      {!hasVideoUrl(selectedMember.videoUrl) ? (
                        <div className="w-full h-full flex items-center justify-center p-4 text-center">
                          <p className="text-gray-400 text-sm">
                            No vehicle showcase available for this member.
                          </p>
                        </div>
                      ) : isEmbeddableVideo(selectedMember.videoUrl) ? (
                          <iframe
                            src={selectedMember.videoUrl}
                            title={`${selectedMember.name}'s Vehicle`}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                      ) : (
                        <video
                          src={selectedMember.videoUrl}
                          controls
                          controlsList="nodownload"
                          disablePictureInPicture
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    {hasVideoUrl(selectedMember.videoUrl) && selectedMember.videoUrl?.includes('dQw4w9WgXcQ') && (
                      <p className="text-gray-500 text-sm mt-2 text-center">
                        * Placeholder video - actual content coming soon
                      </p>
                    )}
                  </div>
                </div>

                {/* Right Side - Details */}
                <div className="lg:w-3/5 p-6 lg:p-8">
                  {/* Name & Role */}
                  <div className="mb-6">
                    <h3 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                      {selectedMember.name}
                    </h3>
                    <span className="inline-block px-4 py-1.5 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
                      {selectedMember.role}
                    </span>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-xl">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <Briefcase size={18} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Role</p>
                        <p className="text-white font-medium">{selectedMember.role}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-xl">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <MapPin size={18} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Based In</p>
                        <p className="text-white font-medium">{selectedMember.location}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-xl sm:col-span-2">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Car size={18} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Chase Vehicle</p>
                        <p className="text-white font-medium">{selectedMember.vehicle}</p>
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  <div className="border-t border-gray-800 pt-6">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <User size={18} className="text-red-400" />
                      About
                    </h4>
                    <p className="text-gray-300 leading-relaxed">
                      {selectedMember.bio}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default TeamSection;
